import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2, CheckCircle2, XCircle, Send, Star } from "lucide-react";
import api, { apiError } from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { fmtDateTime } from "../../../lib/time";
import { Label } from "../../../components/common";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Switch } from "../../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import EmailLog from "./EmailLog";

const NAMES = { ses: "Amazon SES", turbosmtp: "TurboSMTP" };

function ProviderCard({ k, p, primary, onPrimary, testTo, busy, onTest }) {
  const isPrimary = primary === k;
  return (
    <div className={`rounded-xl border bg-white p-5 ${isPrimary ? "border-indigo-300 ring-2 ring-indigo-100" : "border-slate-200"}`} data-testid={`email-provider-${k}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-head text-lg font-bold text-slate-900">{NAMES[k]}</p>
          <p className={`mt-0.5 flex items-center gap-1 text-sm font-semibold ${p.connected ? "text-emerald-600" : "text-slate-400"}`} data-testid={`email-${k}-connected`}>
            {p.connected ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}{p.connected ? `Connected${p.mode ? ` · ${p.mode}` : ""}` : "Not connected"}
          </p>
        </div>
        {isPrimary ? <span className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-3 py-1 text-xs font-bold text-white" data-testid={`email-${k}-primary-badge`}><Star className="h-3 w-3" />Primary</span>
          : <Button size="sm" variant="outline" disabled={!p.connected || busy} onClick={() => onPrimary(k)} data-testid={`email-${k}-make-primary`}>Make primary</Button>}
      </div>
      <dl className="mt-4 space-y-1.5 text-sm">
        <div className="flex justify-between gap-3"><dt className="text-slate-500">Sends from</dt><dd className="font-semibold text-slate-800">{p.from || "—"}</dd></div>
        {p.region && <div className="flex justify-between gap-3"><dt className="text-slate-500">Region</dt><dd className="font-semibold text-slate-800">{p.region}</dd></div>}
        <div className="flex justify-between gap-3"><dt className="text-slate-500">Last success</dt><dd className="text-slate-700" data-testid={`email-${k}-last-success`}>{p.last_success_at ? fmtDateTime(p.last_success_at) : "—"}</dd></div>
        <div className="flex justify-between gap-3"><dt className="text-slate-500">Last failure</dt><dd className="text-right text-slate-700" data-testid={`email-${k}-last-failure`}>{p.last_failure_at ? fmtDateTime(p.last_failure_at) : "—"}</dd></div>
        {p.last_failure_at && p.last_error && <p className="rounded-md bg-red-50 p-2 text-xs text-red-700">{p.last_error}</p>}
      </dl>
      <Button size="sm" variant="outline" className="mt-4" disabled={!p.connected || !testTo || !!busy} onClick={() => onTest(k)} data-testid={`email-${k}-test`}>
        {busy === `test-${k}` ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}Send test email
      </Button>
    </div>
  );
}

export default function PlatformEmail() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [testTo, setTestTo] = useState(user?.email || "");
  useEffect(() => { api.get("/platform/email-settings").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, []);

  const save = async (patch) => {
    setBusy("save");
    try { const { data } = await api.put("/platform/email-settings", { primary: d.primary, failover: d.failover, ...patch }); setD(data); toast.success("Email settings saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const test = async (provider) => {
    setBusy(`test-${provider}`);
    try { await api.post("/platform/email-settings/test", { provider, to: testTo }); toast.success(`Test sent via ${NAMES[provider]} to ${testTo}`); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); api.get("/platform/email-settings").then((r) => setD(r.data)).catch(() => {}); }
  };

  if (!d) return <div className="text-slate-400">Loading…</div>;
  const other = d.primary === "ses" ? "turbosmtp" : "ses";
  return (
    <div className="space-y-6">
      <div><Label>Platform</Label><h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Email Delivery</h1>
        <p className="mt-1 text-sm text-slate-500">Choose which provider sends all Servantage emails, retry through the other one when it fails, and see every email sent.</p></div>

      <Tabs defaultValue="settings">
        <TabsList><TabsTrigger value="settings" data-testid="email-tab-settings">Settings</TabsTrigger><TabsTrigger value="log" data-testid="email-tab-log">Delivery log</TabsTrigger></TabsList>
        <TabsContent value="log" className="mt-5"><EmailLog /></TabsContent>
        <TabsContent value="settings" className="mt-5 space-y-6">

      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="email-failover-card">
        <div className="flex items-start gap-3">
          <Mail className="mt-0.5 h-5 w-5 text-indigo-600" />
          <div>
            <p className="font-head font-bold text-slate-900">Fail over to {NAMES[other]}</p>
            <p className="text-sm text-slate-500">If {NAMES[d.primary]} can't send an email, it's retried straight away through {NAMES[other]}.</p>
          </div>
        </div>
        <Switch checked={d.failover} disabled={busy === "save"} onCheckedChange={(v) => save({ failover: v })} data-testid="email-failover-toggle" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-slate-700">Send test emails to</span>
        <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="w-72" data-testid="email-test-to" />
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        {["ses", "turbosmtp"].map((k) => (
          <ProviderCard key={k} k={k} p={d.providers[k]} primary={d.primary} onPrimary={(v) => save({ primary: v })} testTo={testTo} busy={busy} onTest={test} />
        ))}
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
